<template>
  <div>
    <sp-container
      ref="dropzone"
      v-preserve-properties:rowgap="settings.rowgap"
      flex
      direction="column"
      class="layout-builder-view dropzone"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <div v-for="(row, index) in rows" :key="index" class="item">
        <RowContainer :row="row" :index="index" />
      </div>
      <DropGhost v-if="showDropGhost" :style="dropGhostStyle" class="drop-ghost-overlay" />
    </sp-container>
    <CodeConsole :model="codeConsoleModel" />
  </div>
</template>

<script setup>
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { useSortable } from "@vueuse/integrations/useSortable";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { Context } from "#ce/components/SpLayoutBuilder/constants";
import { useLayoutBuilderStore } from "#ce/components/SpLayoutBuilder/store/layout-builder";
import CodeConsole from "./CodeConsole.ce.vue";
import DropGhost from "./DropGhost.ce.vue";
import RowContainer from "./RowContainer.ce.vue";

const DROP_EFFECT = "copy";

const { settings, rows, loadData, createAndInsertNewRow } = useLayoutBuilderStore({
  context: Context.IFRAME,
});

const codeConsoleModel = computed(() => ({ rows: rows.value, settings: settings.value }));

const dropzone = useTemplateRef("dropzone");
const showDropGhost = ref(false);
const dropGhostPosition = ref({ targetIndex: null, insertBefore: true });
const mouseY = ref(0);

useSortable(dropzone, rows, {
  animation: 150,
  ghostClass: "sortable-ghost",
  chosenClass: "sortable-chosen",
  dragClass: "sortable-drag",
});

onMounted(async () => {
  await loadData();
});

const dropGhostStyle = computed(() => {
  if (!showDropGhost.value || !dropzone.value) {
    return { display: "none" };
  }

  const { targetIndex, insertBefore } = dropGhostPosition.value;
  const itemElements = dropzone.value.querySelectorAll(".item");

  if (!targetIndex || !itemElements || itemElements.length === 0) {
    return { position: "absolute", top: "0px", left: "0px", width: "100%", zIndex: 1000 };
  }

  const dropZoneRect = dropzone.value.getBoundingClientRect();
  let ghostTop = 0;

  if (targetIndex >= itemElements.length) {
    // Insert at the end
    const lastElement = itemElements[itemElements.length - 1];
    const lastRect = lastElement.getBoundingClientRect();
    ghostTop = lastRect.bottom - dropZoneRect.top;
  } else {
    const targetElement = itemElements[targetIndex];
    const targetRect = targetElement.getBoundingClientRect();

    if (insertBefore) {
      ghostTop = targetRect.top - dropZoneRect.top;
    } else {
      ghostTop = targetRect.bottom - dropZoneRect.top;
    }
  }

  return {
    position: "absolute",
    top: `${ghostTop}px`,
    left: "0px",
    width: "100%",
    zIndex: 1000,
    pointerEvents: "none",
  };
});

/**
 * Calculates where an element should be dropped based on mouse position
 * @param {number} mouseY - Mouse Y position relative to drop zone
 * @param {DOMRect} dropZoneRect - Drop zone bounding rectangle
 * @returns {{targetIndex: number, insertBefore: boolean}} Drop position info
 */
function getDropPosition(mouseY, dropZoneRect) {
  const itemElements = dropzone.value.querySelectorAll(".item");

  if (!itemElements || itemElements.length === 0) {
    return { targetIndex: 0, insertBefore: true };
  }

  // Check each element's boundaries
  for (let i = 0; i < itemElements.length; i++) {
    const element = itemElements[i];
    const elementRect = element.getBoundingClientRect();
    const elementTop = elementRect.top - dropZoneRect.top;
    const elementBottom = elementRect.bottom - dropZoneRect.top;
    const elementCenter = elementTop + (elementBottom - elementTop) / 2;

    // If mouse is within this element's bounds
    if (mouseY >= elementTop && mouseY <= elementBottom) {
      // Mouse is in top half - insert before this element
      if (mouseY < elementCenter) {
        return { targetIndex: i, insertBefore: true };
      }
      // Mouse is in bottom half - insert after this element
      return { targetIndex: i, insertBefore: false };
    }

    // If mouse is above this element, insert before it
    if (mouseY < elementTop) {
      return { targetIndex: i, insertBefore: true };
    }
  }

  // If we get here, mouse is below all elements, so insert at the end
  return { targetIndex: itemElements.length, insertBefore: true };
}

// Event handlers
function handleDragOver(e) {
  e.preventDefault();
  e.dataTransfer.dropEffect = DROP_EFFECT;

  dropzone.value.classList.add("drop-highlight");

  const dropZoneRect = dropzone.value.getBoundingClientRect();
  const calculatedMouseY = e.clientY - dropZoneRect.top;

  mouseY.value = calculatedMouseY;

  const newPosition = getDropPosition(calculatedMouseY, dropZoneRect);

  // Only update position if it actually changed to prevent unnecessary re-renders
  if (
    newPosition.targetIndex !== dropGhostPosition.value.targetIndex ||
    newPosition.insertBefore !== dropGhostPosition.value.insertBefore
  ) {
    dropGhostPosition.value = newPosition;
  }

  showDropGhost.value = true;
}

function handleDragLeave() {
  dropzone.value.classList.remove("drop-highlight");
  showDropGhost.value = false;
}

function handleDrop(e) {
  e.preventDefault();
  showDropGhost.value = false;

  const component = e.dataTransfer.getData("application/x-component-type")?.trim();

  if (component) {
    insertNewRow(component);
  }
}

function insertNewRow(component) {
  dropzone.value.classList.remove("drop-highlight");

  // Calculate the correct index for insertion
  let insertIndex = rows.value.length;
  const targetIndex = dropGhostPosition.value.targetIndex;

  if (targetIndex !== null) {
    insertIndex = dropGhostPosition.value.insertBefore ? targetIndex : targetIndex + 1;
  }

  createAndInsertNewRow({ component }, insertIndex);
}
</script>

<style lang="scss">
.layout-builder-view {
  background: hsla(300, 70%, 70%, 0.1);
  position: relative;
  min-height: var(--sp-ref-scale-48);

  .drop-ghost-overlay {
    pointer-events: none;
    z-index: 1000;
  }
}
.item {
  &:nth-child(even) {
    --row-index-tab-left: 100%;
    --row-index-tab-radius: 0 4px 4px 0;
  }
}
</style>

<style>
/* Sortable styles */
.sortable-ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.sortable-chosen {
  background: #f0f8ff;
}

.sortable-drag {
  opacity: 0.8;
  transform: rotate(5deg);
}

.drop-preview {
  position: absolute;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #007bff;
  z-index: 1000;
}
</style>
